import React, { useContext, useMemo, useState } from 'react';
import styles from './StackedLine.module.scss';
import {
  StackedLineConfig,
  StackedLineData,
  WidgetFilterOption
} from '../../../../Models';
import { AppearanceContext, MediaContext } from 'ui-smartforce-settings';
import { SFGrey, SFScrollable } from 'sfui';
import { EChart } from '../../../EChart/EChart';
import { EChartsOption } from 'echarts';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { WidgetFilterDrawer } from '../../WidgetFilterDrawer/WidgetFilterDrawer';
import { WidgetFiltersPanel } from '../../WidgetFiltersPanel/WidgetFiltersPanel';
import { tooltipFormatter } from '../../../../Helpers';
import { FilterSelector } from '../../FilterSelector/FilterSelector';

function getOptionsFromData(data: StackedLineData[]): WidgetFilterOption[] {
  return data.map((d) => ({
    label: d.name,
    color: d.color
  }));
}

function getChartOption(
  config: StackedLineConfig,
  checked: string[],
  dayMode: boolean
): EChartsOption {
  return {
    grid: {
      left: 0,
      right: 0
    },
    tooltip: {
      className: 'echartsTooltip',
      enterable: true,
      confine: true,
      trigger: 'axis',
      position: (point) => {
        return [point[0], 0];
      },
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: dayMode
            ? 'rgba(204, 204, 204, 0.3)'
            : 'rgba(128, 128, 128, 0.3)'
        }
      },
      backgroundColor: dayMode ? '#333' : '#CCC',
      borderWidth: 0,
      formatter: (params) => {
        return tooltipFormatter(
          params as CallbackDataParams[],
          config.data_label,
          config.data_label_singular,
          dayMode
        );
      }
    },
    xAxis: {
      data: config.categories,
      type: 'category',
      boundaryGap: false,
      axisLabel: {
        margin: 18,
        color: dayMode ? SFGrey[600] : SFGrey[400]
      },
      axisTick: {
        show: false
      },
      axisLine: {
        lineStyle: {
          color: dayMode ? SFGrey[200] : SFGrey[700],
          width: 1
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        margin: 12,
        color: dayMode ? SFGrey[600] : SFGrey[400]
      },
      splitLine: {
        lineStyle: {
          color: dayMode ? SFGrey[100] : SFGrey[800],
          type: [6, 4]
        }
      }
    },
    series: config.data
      .filter((d) => checked.includes(d.name))
      .map((d) => ({
        name: d.name,
        data: d.values,
        stack: 'Total',
        type: 'line',
        symbol: 'circle',
        symbolSize: 8,
        emphasis: {
          scale: 1.5
        },
        lineStyle: {
          color: dayMode ? d.color.day : d.color.night,
          width: 4,
          type: 'solid'
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: dayMode ? 'white' : '#1f1f1f',
          color: dayMode ? d.color.day : d.color.night
        }
      }))
  };
}

export interface StackedLineProps {
  title: string;
  config: StackedLineConfig;
  isDrawerOpen?: boolean;
  onDrawerClose?: () => void;
}

export const StackedLine = ({
  title,
  config,
  isDrawerOpen = false,
  ...props
}: StackedLineProps): React.ReactElement<StackedLineProps> => {
  const { isPhone } = useContext(MediaContext);
  const { themeType } = React.useContext(AppearanceContext);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [checked, setChecked] = useState<string[]>(
    getOptionsFromData(config.data).map((o) => o.label)
  );
  const options = useMemo(() => getOptionsFromData(config.data), [config.data]);

  const onDrawerClose = () => {
    props.onDrawerClose && props.onDrawerClose();
  };

  const onDrawerSave = (checked: string[]) => {
    setChecked(checked);
    props.onDrawerClose && props.onDrawerClose();
  };

  return (
    <SFScrollable
      className={styles.scrollable}
      containerClassName={styles.stackedLine}
    >
      <EChart
        className={styles.echart}
        option={getChartOption(config, checked, themeType === 'day')}
      />

      <FilterSelector
        className={styles.filterSelectorForDownload}
        options={options}
        value={checked}
        onChange={(newChecked: string[]) => setChecked(newChecked)}
      />

      {!isPhone && (
        <WidgetFiltersPanel
          isOpen={isPanelOpen}
          options={options}
          checked={checked}
          onChange={(newChecked: string[]) => setChecked(newChecked)}
          onToggle={() => setIsPanelOpen((open: boolean) => !open)}
        />
      )}

      {isPhone && (
        <WidgetFilterDrawer
          title={title}
          defaultChecked={checked}
          options={options}
          isOpen={isDrawerOpen}
          onClose={onDrawerClose}
          onSave={onDrawerSave}
        />
      )}
    </SFScrollable>
  );
};
